import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Button, { ButtonType } from 'inquiry/components/Button';
import { useApply } from 'inquiry/lib/jsonLogic';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ButtonActionComponent } from 'lib/interfaces/ui-components';
import { captureException } from 'lib/reporting';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface ButtonActionProps {
  component: ButtonActionComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

const ButtonAction = ({ component, inquiry, actionsApi, inquiryLoading }: ButtonActionProps) => {
  const { 'button-type': buttonType, disabled, hidden, text } = component.attributes;

  const [t] = useTranslation();

  const {
    loading,
    error,
    callback: handleSubmit,
  } = useApiCallback(
    async (_e: MouseEvent<HTMLButtonElement>) => {
      await actionsApi.onActionButtonClick(component.name);
    },
    [actionsApi.onActionButtonClick, component.name],
  );

  useApiErrorHandler({
    error,
    onNetworkError: () => actionsApi.showMessage(t('errors.network')),
    onError: (err) => {
      if (err instanceof ApiResponseError && err.status === 422) {
        actionsApi.showMessage('Invalid configuration. Please check your parameters.');
      } else {
        captureException(err);
      }
    },
  });

  const apply = useApply(component.name, inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  if (isHidden) {
    return null;
  }

  // Note: it's best practice to ensure the component that disables the button
  // is required. So if a user manually submits via devtools or something, the
  // transition will still fail. Without this, the button disable is a purely
  // UI level thing and can be trivially bypassed.
  const isDisabled = loading || inquiryLoading || apply<boolean>(disabled);

  return (
    <Button
      id={component.name}
      type='button'
      buttonType={buttonType ?? ButtonType.Primary}
      onClick={handleSubmit}
      disabled={isDisabled}
      isPending={loading}
      styles={component?.styles}
    >
      {text}
    </Button>
  );
};

export default ButtonAction;
